import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { flowRight as compose } from 'lodash';
import { injectIntl } from 'react-intl';
import Grid from 'react-bootstrap/lib/Grid';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import withStyles from 'isomorphic-style-loader/withStyles';

import Reviews from '../../components/Reviews';
import ReviewsByYou from '../../components/Reviews/ReviewsByYou';
import SideMenuTrips from '../../components/ManageListing/SideMenuTrips';

import UserReviewsQuery from './UserReviews.graphql';
import PendingReviewsQuery from './PendingReviews.graphql';
import messages from '../../locale/messages';

import s from './ReviewsContainer.css';
import { withQuery } from '../../hoc/withGraphql';

const ReviewsContainer = ({
  intl,
  userReviewsData,
  pendingReviewsData,
  type,
}) => {
  const { formatMessage, locale } = intl;
  const [load, setLoad] = useState(true);
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);

  const prevLocaleRef = useRef(locale);
  const timeoutRef = useRef(null);
  const { pendingReviews, fetchMore } = pendingReviewsData;

  useEffect(() => {
    if (locale !== prevLocaleRef.current) {
      setLoad(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setLoad(true), 1);
      prevLocaleRef.current = locale;
    }
    return () => clearTimeout(timeoutRef.current);
  }, [locale]);

  const loadMore = async (ownerType, current, searchKey) => {
    setIsReviewLoading(true);
    const { userReviews, fetchMore } = userReviewsData;
    await fetchMore({
      query: UserReviewsQuery,
      variables: {
        ownerType,
        offset: userReviews.results.length,
        loadCount: 3,
        current,
        searchKey,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return {
            userReviews: {
              totalCount: previousResult.userReviews.totalCount,
              count: previousResult.userReviews.count,
              results: previousResult.userReviews.results,
            },
          };
        }
        return {
          userReviews: {
            totalCount: fetchMoreResult.userReviews.totalCount,
            count: fetchMoreResult.userReviews.count,
            results: [...previousResult.userReviews.results, ...fetchMoreResult.userReviews.results],
          },
        };
      },
    });
    setIsReviewLoading(false);
  };

  const loadMoreListing = async () => {
    const { reservationData } = pendingReviews;
    setIsListLoading(true);
    await fetchMore({
      query: PendingReviewsQuery,
      variables: {
        offset: reservationData.length,
        loadCount: 10,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return {
            pendingReviews: {
              count: previousResult.pendingReviews.count,
              reservationData: previousResult.pendingReviews.reservationData,
            }
          }
        }
        return {
          pendingReviews: {
            count: fetchMoreResult.pendingReviews.count,
            reservationData: [
              ...previousResult.pendingReviews.reservationData,
              ...fetchMoreResult.pendingReviews.reservationData,
            ],
          },
        };
      },
    });
    setIsListLoading(false);
  };
  

  return (
    <Grid fluid className="dashBoardContainer">
      <Row>
        <Col xs={12}>
          <div className="dashBoardListingGrid">
            <SideMenuTrips
              menuItemOne={formatMessage(messages.reviewsAboutYou)}
              menuItemTwo={formatMessage(messages.reviewPanelTitle2)}
              linkOne="/user/reviews/about-you"
              linkTwo="/user/reviews/you"
            />
            {load && type === 'about-you' && (
              <Reviews
                reviewsData={userReviewsData}
                loadMore={loadMore}
                isReviewLoading={isReviewLoading}
              />
            )}
            {load && type === 'you' && (
              <ReviewsByYou
                reviewsData={userReviewsData}
                pendingData={pendingReviewsData}
                loadMore={loadMore}
                loadMoreListing={loadMoreListing}
                isListLoading={isListLoading}
                isReviewLoading={isReviewLoading}
              />
            )}
          </div>
        </Col>
      </Row>
    </Grid>
  );
}

ReviewsContainer.propTypes = {
  intl: PropTypes.object.isRequired,
  userReviewsData: PropTypes.shape({
    loading: PropTypes.bool,
    userReviews: PropTypes.object,
    fetchMore: PropTypes.func,
  }),
  pendingReviewsData: PropTypes.shape({
    loading: PropTypes.bool,
    pendingReviews: PropTypes.object,
    fetchMore: PropTypes.func,
  }),
  type: PropTypes.string,
};

export default compose(
  injectIntl,
  withStyles(s),
  withQuery(UserReviewsQuery, {
    name: 'userReviewsData',
    options: (props) => ({
      variables: {
        ownerType: props?.type == 'you' ? 'me' : 'others',
        current: 'responded',
      },
      fetchPolicy: 'network-only',
      ssr: false,
    }),
  }),
  withQuery(PendingReviewsQuery, {
    name: 'pendingReviewsData',
    options: () => ({
      fetchPolicy: 'network-only',
      ssr: false,
    }),
  }),
)(ReviewsContainer);